import { Household, SongNumber } from './model.ts';

export function ensure(value: any, message?: string): asserts value {
  if (!value) {
    throw new Error(message ?? `value not truthy`);
  }
}

export function ensureExists<T>(value: T | undefined | null, msg?: string): T {
  if (typeof value === 'undefined' || value === null) {
    throw new Error(msg ?? 'Expected value to be defined and not null');
  }
  return value;
}

export function ensureUnreachable(value: never, message?: string): never {
  throw new Error(`Unreachable code (${value}) ${message}`);
}

export function userVisibleSongNumber(number: number): SongNumber {
  const decimal = number % 1;
  const suffix = (decimal ? String.fromCharCode(Math.round(decimal * 100) + 64) : '').toLowerCase();
  return Math.floor(number).toString() + suffix;
}

export function convertMaybeNumericSongNumber(songNumber: string): SongNumber {
  if (Number.isFinite(Number(songNumber))) {
    return userVisibleSongNumber(Number(songNumber));
  }
  return songNumber;
}

export function isValidEmail(email?: string): email is string {
  if (!email) {
    return false;
  }
  return Boolean(
    String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      ),
  );
}

// a trimmed email, or undefined for none (GitHub issue #4104)
export function normalizeEmail(email: string | null | undefined): string | undefined {
  const trimmed = email?.trim();
  return trimmed ? trimmed : undefined;
}

// whether two emails name the same address: emails sign in ignoring case and surrounding spaces, and an absent
// or blank email matches nothing (GitHub issue #4104)
export function isSameEmail(a: string | null | undefined, b: string | null | undefined): boolean {
  const normalizedA = normalizeEmail(a)?.toLowerCase();
  return Boolean(normalizedA) && normalizedA === normalizeEmail(b)?.toLowerCase();
}

export function objectsEqual(lhs: any, rhs: any) {
  return JSON.stringify(lhs) === JSON.stringify(rhs);
}

// structural equality for JSON-shaped values - unlike objectsEqual() above, key order does not matter.
// A key whose value is undefined is treated as absent, because that is how the value serializes to JSON
// (so two values that compare equal here are indistinguishable once stored or sent over the wire).
export function deepEqual(lhs: unknown, rhs: unknown): boolean {
  if (lhs === rhs) {
    return true;
  }
  if (typeof lhs !== 'object' || typeof rhs !== 'object' || lhs === null || rhs === null) {
    return false;
  }
  if (Array.isArray(lhs) || Array.isArray(rhs)) {
    if (!Array.isArray(lhs) || !Array.isArray(rhs) || lhs.length !== rhs.length) {
      return false;
    }
    return lhs.every((value, index) => deepEqual(value, rhs[index]));
  }
  const lhsRecord = lhs as Record<string, unknown>;
  const rhsRecord = rhs as Record<string, unknown>;
  const lhsKeys = definedKeys(lhsRecord);
  if (lhsKeys.length !== definedKeys(rhsRecord).length) {
    return false;
  }
  // a key missing from rhs reads as undefined, which never deep-equals a defined lhs value
  return lhsKeys.every((key) => deepEqual(lhsRecord[key], rhsRecord[key]));
}

function definedKeys(value: Record<string, unknown>): string[] {
  return Object.keys(value).filter((key) => value[key] !== undefined);
}

export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// used to determine if two objects are equivalent or if an object instance has changed (been recreated)
// Usages:  objectId(obj1) === objectId(obj2)  - OR-  console.log(objectId(obj))
export const objectId = (() => {
  let currentId = 0;
  const map = new WeakMap();

  return (object: any) => {
    if (!map.has(object)) {
      map.set(object, ++currentId);
    }

    return map.get(object);
  };
})();

export type NonEmptyArray<T> = [T, ...T[]];

export async function assertThrowsAsync(callback: () => Promise<void>, message?: string) {
  let exceptionThrown: boolean;
  try {
    await callback();
    exceptionThrown = false;
  } catch {
    exceptionThrown = true;
  }
  if (!exceptionThrown) {
    throw new Error(message ?? 'Expected exception');
  }
}

export function getUnknownHouseholdCredentials(
  token: string,
): Pick<Household, 'familyLastName' | 'householdEmail' | 'users'> {
  return { familyLastName: 'Unknown', householdEmail: `${token}@unknown.com`, users: [] };
}

// Seeded pseudo random number generator
export function mulberry32(seed: number) {
  return function (): number {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function mulberry32Int(seed: number) {
  return function (): number {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return (t ^ (t >>> 14)) >>> 0;
  };
}

export function round(value: number, decimals: number) {
  return parseFloat(value.toFixed(decimals));
}

/**
 * The current Unix timestamp.
 */
export function unixTimestamp(): number {
  return Number(BigInt(new Date().valueOf()) / 1_000n);
}

/**
 * The UTC offset of the client, in seconds.
 */
export function utcOffsetSeconds(): number {
  return new Date().getTimezoneOffset() * 60;
}

// determines if two floating point numbers are "close enough" -> e.g. triplets cause rounding errors
export function isClose(lhs: number, rhs: number) {
  return Math.abs(lhs - rhs) <= durationThreshold;
}

const durationThreshold = 0.0000001;

export function zipArrayWithLookAhead<T>(array: T[]): [T, T | undefined][] {
  return array.map((value, i) => [value, array[i + 1]]);
}

// ['a'] => 'a', ['a', 'b'] => 'a and b', ['a', 'b', 'c'] => 'a, b, or c'
export function joinWithConjunction(items: string[], conjunction: string): string {
  if (items.length <= 1) return items.join('');
  if (items.length === 2) return items.join(` ${conjunction} `);
  return `${items.slice(0, -1).join(', ')}, ${conjunction} ${items.at(-1)}`;
}

export function naturalStringCompare(left: string, right: string): number {
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
}

// Server-side ordering that must not vary with the machine's locale (manifest bytes and
// hashes depend on it, issue #3543). Clients keep using naturalStringCompare so they sort
// for their own locale.
export function naturalStringCompareEn(left: string, right: string): number {
  return left.localeCompare(right, 'en', { numeric: true, sensitivity: 'base' });
}
